// ============================================================
// TMR Portal Admin — the Empresa's contact e-mail: where it comes from, and what it resolves to
// ============================================================
import type { CompanyRecord, ContactEmailSource } from './contract';
import type { CompanyForm } from './form';

/** How each source reads on screen, after the address ("contato@x.com · do representante"). */
const ORIGIN_LABEL: Record<ContactEmailSource, string> = {
  Representative: 'do representante',
  FocalContact: 'do contato focal',
  Other: 'outro e-mail',
};

/**
 * What a source is called beside the resolved address, falling back to the raw key for one this build
 * does not know yet.
 *
 * @param source The source key.
 */
export function contactEmailOriginLabel(source: ContactEmailSource | string): string {
  return ORIGIN_LABEL[source as ContactEmailSource] ?? source;
}

/** The contact e-mail as the screens show it: the address (null when there is none yet) and its origin. */
export interface ResolvedContactEmail {
  email: string | null;
  source: ContactEmailSource;
}

/**
 * The contact e-mail the form declares, resolved the way the backend will resolve it on save.
 *
 * @param form The registration as it stands.
 */
export function formContactEmail(form: CompanyForm): ResolvedContactEmail {
  return resolve(form.contactEmailSource, form.representativeEmail, form.focalContactEmail, form.contactEmail);
}

/**
 * The contact e-mail a stored record declares. The people's e-mails are the directory's answer at read
 * time, so a reference that no longer resolves leaves it null.
 *
 * @param record The Empresa as `GET /companies/{id}` returned it.
 */
export function recordContactEmail(record: CompanyRecord): ResolvedContactEmail {
  return resolve(
    record.contactEmailSource ?? 'Representative',
    record.representative?.email ?? null,
    record.focalContact?.email ?? null,
    record.contactEmail,
  );
}

/**
 * Picks the address the source points at; blank reads as none.
 *
 * @param source The declared source.
 * @param representative The representative's e-mail.
 * @param focalContact The focal contact's e-mail.
 * @param other The typed address for `Other`.
 */
function resolve(
  source: ContactEmailSource,
  representative: string | null,
  focalContact: string | null,
  other: string | null,
): ResolvedContactEmail {
  const raw = source === 'FocalContact' ? focalContact : source === 'Other' ? other : representative;
  const email = raw?.trim() || null;
  return { email, source };
}

/**
 * The one line the review and the detail show: "contato@x.com · do representante", or a dash with the
 * origin when the chosen person has no e-mail yet.
 *
 * @param resolved The resolved contact e-mail.
 */
export function contactEmailSummary(resolved: ResolvedContactEmail): string {
  return `${resolved.email ?? '—'} · ${contactEmailOriginLabel(resolved.source)}`;
}
